<template>
  <div class="face-device-v3-manage">
    <div class="toolbar">
      <div class="shop-title">
        <strong>{{ shopName || ('校区 ' + shopId) }}</strong>
        <span>已绑定 {{ devices.length }}/2 台</span>
      </div>
      <div class="toolbar-actions">
        <el-button :disabled="enabledDevices.length === 0 || syncingAll" :loading="syncingAll" type="primary" @click="syncAllDevices">补全全部设备人脸</el-button>
        <el-button type="success" :disabled="devices.length >= 2" @click="openCreate">添加 V3 设备</el-button>
      </div>
    </div>

    <el-alert title="人脸保存在设备本地。两台设备都需要录入时，请使用“补全全部设备人脸”，系统会从平台人脸资料分别下发到每台启用设备。" type="warning" :closable="false" show-icon />

    <div v-if="devices.length" class="device-grid">
      <el-card v-for="device in devices" :key="device.id" class="device-card" shadow="never">
        <div slot="header" class="device-card-header">
          <div>
            <strong>{{ device.device_id }}</strong>
            <el-tag size="mini" :type="device.online == 1 ? 'success' : 'info'">{{ device.online == 1 ? '在线' : '离线' }}</el-tag>
            <el-tag size="mini" :type="device.enabled == 1 ? 'success' : 'danger'">{{ device.enabled == 1 ? '已启用' : '已停用' }}</el-tag>
          </div>
          <el-button type="text" @click="openEdit(device)">编辑绑定</el-button>
        </div>
        <div class="device-meta">
          <div><span>应用版本</span><strong>{{ device.app_version || '-' }}</strong></div>
          <div><span>已应用配置</span><strong>{{ configVersionText(device.config_version) }}</strong></div>
          <div><span>最后活跃</span><strong>{{ device.last_active_at || '-' }}</strong></div>
        </div>
        <div class="device-actions">
          <el-button size="small" :disabled="device.enabled != 1 || syncingAll || isSyncing(device.device_id)" :loading="isSyncing(device.device_id)" type="primary" @click="syncAll(device)">{{ isSyncing(device.device_id) ? '同步中' : '补全本机人脸' }}</el-button>
          <el-dropdown trigger="click" :disabled="device.enabled != 1 || syncingAll || isSyncing(device.device_id)" @command="command => handlePersonCommand(command, device)">
            <el-button size="small" :disabled="device.enabled != 1 || syncingAll">单人管理<i class="el-icon-arrow-down el-icon--right" /></el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="syncOne">同步单人</el-dropdown-item>
              <el-dropdown-item command="deleteOne">删除单人</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <el-button size="small" :disabled="device.enabled != 1" @click="openConfig(device)">初始化配置</el-button>
          <el-button size="small" :disabled="device.enabled != 1" @click="checkUpdate(device)">检查更新</el-button>
          <el-button size="small" @click="openCommands(device)">命令状态</el-button>
          <el-button size="small" @click="toggleEnabled(device)">{{ device.enabled == 1 ? '停用' : '启用' }}</el-button>
          <el-button size="small" type="danger" @click="deleteDevice(device)">逻辑删除</el-button>
        </div>
      </el-card>
    </div>
    <div v-else class="empty-device">
      <i class="el-icon-monitor" />
      <p>当前校区还没有 V3 人脸设备</p>
      <el-button type="primary" @click="openCreate">添加第一台设备</el-button>
    </div>

    <el-dialog append-to-body :title="deviceForm.id ? '编辑 V3 设备绑定' : '添加 V3 设备'" :visible.sync="deviceDialogVisible" width="460px">
      <el-form label-width="100px">
        <el-form-item label="设备编号" required>
          <el-input v-model.trim="deviceForm.device_id" :disabled="!!deviceForm.id" maxlength="7" placeholder="T 加 6 位数字，如 T918044" />
        </el-form-item>
        <el-form-item label="所属校区">
          <el-input :value="shopName || shopId" disabled />
        </el-form-item>
        <el-form-item label="启用设备">
          <el-switch v-model="deviceForm.enabled" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="deviceDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveDevice">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog append-to-body custom-class="face-device-v3-config-dialog" :title="'初始化设备配置 - ' + (configDevice ? configDevice.device_id : '')" :visible.sync="configDialogVisible" width="860px" top="4vh">
      <el-alert title="以下为推荐默认值。下发后请在“命令状态”中确认设备回执为执行成功。" type="info" :closable="false" show-icon />
      <div v-loading="configLoading" class="config-form-wrap">
        <el-form v-if="configForm" ref="configForm" :model="configForm" label-width="150px" size="small">
          <div class="config-section">
            <h4><i class="el-icon-view" /> 人脸识别</h4>
            <div class="config-grid">
              <el-form-item label="活体检测"><el-switch v-model="configForm.face.liveness_enabled" /></el-form-item>
              <el-form-item label="陌生人语音"><el-switch v-model="configForm.face.stranger_voice_enabled" /></el-form-item>
              <el-form-item label="活体阈值"><el-input-number v-model="configForm.face.live_threshold" :min="0" :max="1" :step="0.05" :precision="2" /></el-form-item>
              <el-form-item label="匹配阈值"><el-input-number v-model="configForm.face.match_threshold" :min="0" :max="100" :step="1" /></el-form-item>
              <el-form-item label="质量阈值"><el-input-number v-model="configForm.face.quality_threshold" :min="0" :max="1" :step="0.05" :precision="2" /></el-form-item>
              <el-form-item label="最小人脸尺寸"><div class="input-with-unit"><el-input-number v-model="configForm.face.minimum_face_size_px" :min="40" :max="1000" :step="10" /><span class="unit">px</span></div></el-form-item>
              <el-form-item label="稳定帧数"><el-input-number v-model="configForm.face.stable_frames" :min="1" :max="10" /></el-form-item>
              <el-form-item label="识别间隔"><div class="input-with-unit"><el-input-number v-model="configForm.face.recognition_interval_ms" :min="100" :max="60000" :step="100" /><span class="unit">毫秒</span></div></el-form-item>
              <el-form-item label="录入超时"><div class="input-with-unit"><el-input-number v-model="configForm.face.enrollment_timeout_seconds" :min="5" :max="120" /><span class="unit">秒</span></div></el-form-item>
            </div>
          </div>

          <div class="config-section">
            <h4><i class="el-icon-sunny" /> 补光灯与屏幕</h4>
            <div class="config-grid">
              <el-form-item label="补光策略">
                <el-select v-model="configForm.fill_light.strategy">
                  <el-option label="始终关闭" value="always_off" />
                  <el-option label="检测到人脸时开启" value="face_detected" />
                  <el-option label="定时段内检测开启" value="scheduled_face" />
                </el-select>
              </el-form-item>
              <el-form-item label="熄灭延迟"><div class="input-with-unit"><el-input-number v-model="configForm.fill_light.off_delay_seconds" :min="1" :max="60" /><span class="unit">秒</span></div></el-form-item>
              <el-form-item label="开始时间"><el-time-select v-model="configForm.fill_light.start_time" :disabled="configForm.fill_light.strategy !== 'scheduled_face'" :picker-options="timeOptions" /></el-form-item>
              <el-form-item label="结束时间"><el-time-select v-model="configForm.fill_light.end_time" :disabled="configForm.fill_light.strategy !== 'scheduled_face'" :picker-options="timeOptions" /></el-form-item>
              <el-form-item label="屏幕亮度"><el-slider v-model="configForm.display.brightness_percent" :min="10" :max="100" show-input /></el-form-item>
              <el-form-item label="屏幕常亮"><el-switch v-model="configForm.display.keep_screen_on" /></el-form-item>
            </div>
          </div>

          <div class="config-section">
            <h4><i class="el-icon-microphone" /> 语音设置</h4>
            <div class="config-grid">
              <el-form-item label="媒体音量"><el-slider v-model="configForm.speech.media_volume_percent" :min="0" :max="100" show-input /></el-form-item>
            </div>
          </div>

          <div class="config-section">
            <h4><i class="el-icon-chat-dot-round" /> 播报文案</h4>
            <div class="config-grid speech-copy-grid">
              <el-form-item v-for="item in speechCopyFields" :key="item.key" :label="item.label">
                <el-input v-model.trim="configForm.speech_copy[item.key]" maxlength="80" show-word-limit />
              </el-form-item>
            </div>
          </div>

          <div class="config-section">
            <h4><i class="el-icon-camera" /> 考勤抓拍</h4>
            <div class="config-grid">
              <el-form-item label="签到抓拍"><el-switch v-model="configForm.attendance_capture.check_in_enabled" /></el-form-item>
              <el-form-item label="签离抓拍"><el-switch v-model="configForm.attendance_capture.check_out_enabled" /></el-form-item>
            </div>
          </div>

          <el-collapse class="config-json-preview">
            <el-collapse-item title="高级：查看将要下发的 JSON" name="json">
              <pre>{{ configPreview }}</pre>
            </el-collapse-item>
          </el-collapse>
        </el-form>
      </div>
      <span slot="footer">
        <el-button @click="configDialogVisible = false">取消</el-button>
        <el-button :disabled="configLoading" @click="resetConfig">恢复推荐值</el-button>
        <el-button type="primary" :loading="configSubmitting" :disabled="configLoading" @click="submitConfig">确认并下发配置</el-button>
      </span>
    </el-dialog>

    <el-dialog append-to-body custom-class="face-device-v3-command-dialog" :title="'命令状态 - ' + commandQuery.device_id" :visible.sync="commandDialogVisible" width="88%">
      <div class="command-filter">
        <el-select v-model="commandQuery.status" clearable placeholder="全部状态" @change="handleCommandFilter">
          <el-option v-for="item in commandStatuses" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <el-button type="primary" @click="loadCommands">刷新</el-button>
      </div>
      <el-table :data="commands" border stripe>
        <el-table-column prop="message_id" label="消息 ID" min-width="250" />
        <el-table-column label="命令" width="110"><template slot-scope="scope">{{ commandTypeText(scope.row.type) }}</template></el-table-column>
        <el-table-column label="状态" width="110"><template slot-scope="scope">{{ commandStatusText(scope.row.status) }}</template></el-table-column>
        <el-table-column prop="attempt_count" label="发送次数" width="80" align="center" />
        <el-table-column prop="ack_code" label="回执状态" width="100" />
        <el-table-column label="设备回执详情" min-width="220"><template slot-scope="scope"><span :title="commandAckDetail(scope.row)">{{ commandAckDetail(scope.row) }}</span></template></el-table-column>
        <el-table-column label="投递错误" min-width="130"><template slot-scope="scope">{{ commandTransportError(scope.row) }}</template></el-table-column>
        <el-table-column prop="create_time" label="创建时间" width="160" />
        <el-table-column prop="acked_at" label="回执时间" width="160" />
      </el-table>
      <div class="pagination-footer">
        <el-pagination :current-page="commandQuery.page" :page-size="commandQuery.page_size" :pager-count="7" :total="commandTotal" layout="total, prev, pager, next, jumper" @current-change="handleCommandPageChange" />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { commandStatusText, commandTypeText, createDefaultConfig, createSyncTask, getEnabledDevices, mergeConfigTemplate, normalizePageResult } from './faceDeviceV3Helpers'

export default {
  name: 'FaceDeviceV3Manage',
  props: {
    shopId: { type: [String, Number], required: true },
    shopName: { type: String, default: '' }
  },
  data() {
    return {
      devices: [],
      syncingAll: false,
      deviceDialogVisible: false,
      deviceForm: { id: '', device_id: '', enabled: 1 },
      configDialogVisible: false,
      configDevice: null,
      configForm: createDefaultConfig(),
      loadedConfigTemplate: createDefaultConfig(),
      configLoading: false,
      configSubmitting: false,
      configRequestId: 0,
      timeOptions: { start: '00:00', step: '00:30', end: '23:30' },
      speechCopyFields: [
        { key: 'recognized', label: '识别成功' },
        { key: 'check_in_success', label: '签到成功' },
        { key: 'check_out_success', label: '签离成功' },
        { key: 'offline', label: '网络断开' },
        { key: 'stranger', label: '陌生人' },
        { key: 'liveness_failed', label: '活体失败' },
        { key: 'enrollment_success', label: '录入成功' },
        { key: 'enrollment_failed', label: '录入失败' }
      ],
      commandDialogVisible: false,
      commands: [],
      commandTotal: 0,
      syncingDevices: {},
      commandQuery: { device_id: '', page: 1, page_size: 10, status: '' },
      commandStatuses: [
        { value: 'pending', label: '等待发送' },
        { value: 'leased', label: '发送中' },
        { value: 'sent', label: '等待回执' },
        { value: 'retry_wait', label: '等待重试' },
        { value: 'acked_success', label: '执行成功' },
        { value: 'acked_failed', label: '执行失败' },
        { value: 'expired', label: '已过期' }
      ]
    }
  },
  computed: {
    enabledDevices() {
      return getEnabledDevices(this.devices)
    },
    configPreview() {
      return JSON.stringify(this.configForm, null, 2)
    }
  },
  created() {
    this.loadDevices()
  },
  methods: {
    commandTypeText,
    commandStatusText,
    configVersionText(version) {
      const appliedVersion = Number(version) || 0
      return appliedVersion > 0 ? `v${appliedVersion}` : '未配置'
    },
    commandAckDetail(row) {
      const detail = row && row.ack_detail
      if (!detail || typeof detail !== 'object') return '-'
      const parts = []
      const errorCode = detail.error_code ? String(detail.error_code) : ''
      const errorMessage = detail.error_message_zh ? String(detail.error_message_zh) : ''
      if (errorMessage) parts.push(errorCode ? `${errorMessage}（${errorCode}）` : errorMessage)
      else if (errorCode) parts.push(errorCode)
      if (Array.isArray(detail.rejected_fields) && detail.rejected_fields.length) parts.push(`拒绝字段：${detail.rejected_fields.join('、')}`)
      if (Array.isArray(detail.warnings) && detail.warnings.length) parts.push(`警告：${detail.warnings.join('、')}`)
      return parts.length ? parts.join('；') : '-'
    },
    commandTransportError(row) {
      if (!row || !row.last_error_code) return '-'
      return row.last_error_code === 'ack_timeout' ? '等待设备回执超时' : row.last_error_code
    },
    ensureSuccess(res) {
      if (!res || res.code != 200) {
        const error = new Error((res && res.desc) || '操作失败')
        error.alreadyShown = !!(res && res.code == 550)
        throw error
      }
      return res
    },
    showError(error) {
      if (error && error.alreadyShown) return
      this.$message.error(error && error.message ? error.message : '操作失败，请稍后重试')
    },
    loadDevices() {
      return this.$http.getFaceDeviceV3List({ shop_id: this.shopId, page: 1, page_size: 2 }).then(res => {
        this.ensureSuccess(res)
        this.devices = normalizePageResult(res.data).list
      }).catch(this.showError)
    },
    openCreate() {
      if (this.devices.length >= 2) {
        this.$message.warning('每个校区最多添加 2 台 V3 设备')
        return
      }
      this.deviceForm = { id: '', device_id: '', enabled: 1 }
      this.deviceDialogVisible = true
    },
    openEdit(row) {
      this.deviceForm = { id: row.id, device_id: row.device_id, enabled: Number(row.enabled) }
      this.deviceDialogVisible = true
    },
    saveDevice() {
      if (!/^T[0-9]{6}$/.test(this.deviceForm.device_id)) {
        this.$message.error('设备编号必须为 T 加 6 位数字，例如 T918044')
        return
      }
      const params = Object.assign({}, this.deviceForm, { shop_id: this.shopId })
      this.$http.saveFaceDeviceV3(params).then(res => {
        this.ensureSuccess(res)
        this.$message.success('设备保存成功')
        this.deviceDialogVisible = false
        this.loadDevices()
      }).catch(this.showError)
    },
    toggleEnabled(row) {
      const enabled = row.enabled == 1 ? 0 : 1
      this.$confirm(`确认${enabled ? '启用' : '停用'}设备 ${row.device_id}？`, '设备状态', { type: 'warning' }).then(() => {
        return this.$http.setFaceDeviceV3Enabled({ id: row.id, enabled })
      }).then(res => {
        this.ensureSuccess(res)
        this.$message.success(enabled ? '设备已启用' : '设备已停用')
        this.loadDevices()
      }).catch(error => {
        if (error !== 'cancel' && error !== 'close') this.showError(error)
      })
    },
    deleteDevice(row) {
      this.$confirm(`逻辑删除会停用设备 ${row.device_id}，历史记录仍会保留。是否继续？`, '逻辑删除', { type: 'warning' }).then(() => {
        return this.$http.deleteFaceDeviceV3({ id: row.id })
      }).then(res => {
        this.ensureSuccess(res)
        this.$message.success('设备已停用并保留历史')
        this.loadDevices()
      }).catch(error => {
        if (error !== 'cancel' && error !== 'close') this.showError(error)
      })
    },
    openConfig(row) {
      const requestId = ++this.configRequestId
      const deviceId = row.device_id
      this.configDevice = row
      this.configDialogVisible = true
      this.configLoading = true
      this.$http.getFaceDeviceV3ConfigTemplate({ device_id: deviceId }).then(res => {
        if (requestId !== this.configRequestId || !this.configDevice || this.configDevice.device_id !== deviceId) return
        this.ensureSuccess(res)
        this.loadedConfigTemplate = mergeConfigTemplate(res.data && res.data.config)
        this.configForm = mergeConfigTemplate(this.loadedConfigTemplate)
      }).catch(error => {
        if (requestId !== this.configRequestId || !this.configDevice || this.configDevice.device_id !== deviceId) return
        this.loadedConfigTemplate = createDefaultConfig()
        this.configForm = mergeConfigTemplate(this.loadedConfigTemplate)
        this.showError(error)
        this.$message.warning('默认配置加载失败，已使用页面内置推荐值')
      }).then(() => {
        if (requestId === this.configRequestId) this.configLoading = false
      })
    },
    resetConfig() {
      this.configForm = mergeConfigTemplate(this.loadedConfigTemplate)
      this.$message.success('已恢复推荐配置')
    },
    validateConfig(config) {
      const copyValues = Object.keys(config.speech_copy).map(key => String(config.speech_copy[key] || '').trim())
      if (copyValues.some(value => !value || value.length > 80)) return '播报文案不能为空且不能超过 80 个字符'
      return ''
    },
    submitConfig() {
      const config = mergeConfigTemplate(this.configForm)
      const error = this.validateConfig(config)
      if (error) {
        this.$message.error(error)
        return
      }
      this.$confirm(`确认向设备 ${this.configDevice.device_id} 下发完整配置？设备成功回执后配置版本才会更新。`, '下发设备配置', { type: 'warning' }).then(() => {
        this.configSubmitting = true
        return this.$http.initializeFaceDeviceV3({ device_id: this.configDevice.device_id, config: JSON.stringify(config) })
      }).then(res => {
        this.ensureSuccess(res)
        this.$message.success(`配置命令已创建，版本 ${res.data.config_version}，请到命令状态确认执行结果`)
        this.configDialogVisible = false
        this.loadDevices()
      }).catch(error => {
        if (error !== 'cancel' && error !== 'close') this.showError(error)
      }).then(() => {
        this.configSubmitting = false
      })
    },
    checkUpdate(row) {
      this.$confirm(`通知设备 ${row.device_id} 检查更新？`, '检查更新').then(() => {
        return this.$http.checkFaceDeviceV3Update({ device_id: row.device_id })
      }).then(res => {
        this.ensureSuccess(res)
        this.$message.success('更新检查命令已创建')
      }).catch(error => {
        if (error !== 'cancel' && error !== 'close') this.showError(error)
      })
    },
    isSyncing(deviceId) {
      return !!(this.syncingDevices[deviceId] && this.syncingDevices[deviceId].running)
    },
    handlePersonCommand(command, row) {
      if (command === 'syncOne') this.promptPerson(row, false)
      if (command === 'deleteOne') this.promptPerson(row, true)
    },
    promptPerson(row, deleting) {
      this.$prompt('请输入学员用户 ID', deleting ? '从设备删除人员' : '同步人员到设备', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPattern: /^\d+$/,
        inputErrorMessage: '请输入有效的学员用户 ID'
      }).then(({ value }) => {
        const method = deleting ? 'deleteFaceDeviceV3Person' : 'syncFaceDeviceV3Person'
        return this.$http[method]({ device_id: row.device_id, user_id: value })
      }).then(res => {
        this.ensureSuccess(res)
        this.$message.success(deleting ? '人员删除命令已创建' : '人员同步命令已创建')
      }).catch(error => {
        if (error !== 'cancel' && error !== 'close') this.showError(error)
      })
    },
    runDeviceSync(row) {
      const previousTask = this.syncingDevices[row.device_id]
      const task = previousTask || createSyncTask(row.device_id)
      task.running = true
      this.$set(this.syncingDevices, row.device_id, task)
      return this.syncNextBatch(task).then(result => {
        result.running = false
        this.$delete(this.syncingDevices, row.device_id)
        return result
      }).catch(error => {
        task.running = false
        error.syncTask = task
        throw error
      })
    },
    syncAll(row) {
      if (this.isSyncing(row.device_id)) {
        this.$message.warning('该设备正在同步人员，请勿重复启动')
        return
      }
      const previousTask = this.syncingDevices[row.device_id]
      const confirmText = previousTask
        ? `上次同步停在游标 ${previousTask.lastUserId || '起点'}，已完成 ${previousTask.batches} 批。是否从失败批次继续？`
        : '系统将每批最多处理 20 名在读学员，并自动继续后续批次。是否开始？'
      this.$confirm(confirmText, '补全本机人脸', { type: 'warning' }).then(() => this.runDeviceSync(row)).then(task => {
        this.$message.success(`设备 ${row.device_id} 补全完成：创建 ${task.created} 条，跳过 ${task.skipped} 人`)
      }).catch(error => {
        if (error === 'cancel' || error === 'close') return
        this.showSyncError(error.syncTask, error)
      })
    },
    syncAllDevices() {
      if (!this.enabledDevices.length || this.syncingAll) return
      this.$confirm(`将平台已有的人脸资料依次补全到 ${this.enabledDevices.length} 台启用设备，每台每批最多处理 20 人。是否开始？`, '补全全部设备人脸', { type: 'warning' }).then(() => {
        this.syncingAll = true
        const summary = { devices: 0, created: 0, skipped: 0 }
        return this.enabledDevices.reduce((chain, device) => chain.then(() => this.runDeviceSync(device)).then(task => {
          summary.devices += 1
          summary.created += task.created
          summary.skipped += task.skipped
        }), Promise.resolve()).then(() => {
          this.$message.success(`全部补全完成：${summary.devices} 台设备，创建 ${summary.created} 条，跳过 ${summary.skipped} 人`)
        })
      }).catch(error => {
        if (error !== 'cancel' && error !== 'close') this.showSyncError(error.syncTask, error)
      }).then(() => {
        this.syncingAll = false
      })
    },
    showSyncError(task, error) {
      if (!task) {
        this.showError(error)
        return
      }
      this.$message.error(`设备 ${task.deviceId} 在第 ${task.batches + 1} 批失败；此前已创建 ${task.created} 条、跳过 ${task.skipped} 人，最后游标 ${task.lastUserId || '起点'}`)
    },
    syncNextBatch(task) {
      if (task.batches >= 10000) return Promise.reject(new Error('同步批次超过安全上限'))
      const requestedCursor = task.lastUserId
      return this.$http.refreshFaceDeviceV3People({
        device_id: task.deviceId,
        batch_id: task.batchId,
        last_user_id: requestedCursor,
        limit: 20
      }).then(res => {
        this.ensureSuccess(res)
        task.created += Number(res.data.created) || 0
        task.skipped += Number(res.data.skipped) || 0
        task.batches += 1
        const nextUserId = res.data.next_user_id === undefined || res.data.next_user_id === null ? '' : String(res.data.next_user_id)
        if (res.data.has_more && (!nextUserId || nextUserId === String(requestedCursor))) {
          throw new Error('同步游标未推进，已停止任务')
        }
        task.lastUserId = nextUserId
        if (res.data.has_more) return this.syncNextBatch(task)
        return task
      })
    },
    openCommands(row) {
      this.commandQuery = { device_id: row.device_id, page: 1, page_size: 10, status: '' }
      this.commandDialogVisible = true
      this.loadCommands()
    },
    loadCommands() {
      return this.$http.getFaceDeviceV3CommandList(this.commandQuery).then(res => {
        this.ensureSuccess(res)
        const page = normalizePageResult(res.data)
        this.commands = page.list
        this.commandTotal = page.count
      }).catch(this.showError)
    },
    handleCommandFilter() {
      this.commandQuery.page = 1
      this.loadCommands()
    },
    handleCommandPageChange(page) {
      this.commandQuery.page = page
      this.loadCommands()
    }
  }
}
</script>

<style scoped>
.face-device-v3-manage .toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 12px;
  gap: 10px;
}
.face-device-v3-manage .shop-title strong { margin-right: 12px; font-size: 16px; }
.face-device-v3-manage .shop-title span { color: #909399; font-size: 13px; }
.face-device-v3-manage .toolbar-actions { display: flex; gap: 8px; }
.face-device-v3-manage .device-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
}
.face-device-v3-manage .device-card { min-width: 0; }
.face-device-v3-manage .device-card-header { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.face-device-v3-manage .device-card-header strong { margin-right: 8px; font-size: 15px; }
.face-device-v3-manage .device-card-header .el-tag { margin-right: 4px; }
.face-device-v3-manage .device-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}
.face-device-v3-manage .device-meta div { min-width: 0; padding: 9px; border-radius: 4px; background: #f5f7fa; }
.face-device-v3-manage .device-meta span { display: block; margin-bottom: 5px; color: #909399; font-size: 12px; }
.face-device-v3-manage .device-meta strong { display: block; overflow: hidden; color: #303133; font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }
.face-device-v3-manage .device-actions { display: flex; flex-wrap: wrap; gap: 7px; }
.face-device-v3-manage .empty-device { padding: 36px 0; color: #909399; text-align: center; }
.face-device-v3-manage .empty-device i { font-size: 36px; }
.face-device-v3-manage .empty-device p { margin: 10px 0 14px; }
.face-device-v3-manage .pagination-footer { display: flex; justify-content: flex-end; margin-top: 16px; }
.face-device-v3-manage .command-filter { display: flex; gap: 10px; margin-bottom: 12px; }
@media (max-width: 1180px) {
  .face-device-v3-manage .device-grid { grid-template-columns: 1fr; }
}
@media (max-width: 760px) {
  .face-device-v3-manage .toolbar-actions { width: 100%; }
  .face-device-v3-manage .toolbar-actions .el-button { flex: 1; margin-left: 0; }
  .face-device-v3-manage .device-meta { grid-template-columns: 1fr; }
}
</style>

<style>
.face-device-v3-config-dialog {
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  margin-bottom: 0;
  overflow: hidden;
}
.face-device-v3-config-dialog .el-dialog__header {
  flex: none;
}
.face-device-v3-config-dialog .el-dialog__body {
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  padding-bottom: 0;
  overflow: hidden;
}
.face-device-v3-config-dialog .config-form-wrap {
  flex: 1;
  min-height: 0;
  margin-top: 14px;
  padding-right: 8px;
  overflow-y: auto;
}
.face-device-v3-config-dialog .config-section {
  margin-bottom: 14px;
  padding: 14px 16px 4px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
}
.face-device-v3-config-dialog .config-section h4 {
  margin: 0 0 16px;
  color: #303133;
  font-size: 15px;
}
.face-device-v3-config-dialog .config-section h4 i {
  margin-right: 6px;
  color: #409eff;
}
.face-device-v3-config-dialog .config-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 22px;
}
.face-device-v3-config-dialog .config-grid .el-select,
.face-device-v3-config-dialog .config-grid .el-input,
.face-device-v3-config-dialog .config-grid .el-input-number {
  width: 100%;
}
.face-device-v3-config-dialog .config-grid .el-slider {
  padding-right: 6px;
}
.face-device-v3-config-dialog .input-with-unit {
  display: flex;
  align-items: center;
  width: 100%;
  white-space: nowrap;
}
.face-device-v3-config-dialog .input-with-unit .el-input-number {
  flex: 1;
  min-width: 0;
  width: auto;
}
.face-device-v3-config-dialog .config-section .unit {
  flex: none;
  width: 32px;
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}
.face-device-v3-config-dialog .config-json-preview {
  margin-bottom: 8px;
}
.face-device-v3-config-dialog .config-json-preview pre {
  max-height: 300px;
  margin: 0;
  padding: 12px;
  overflow: auto;
  color: #303133;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
}
.face-device-v3-config-dialog .el-dialog__footer {
  flex: none;
  border-top: 1px solid #ebeef5;
  background: #fff;
}
.face-device-v3-command-dialog .pagination-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
.face-device-v3-command-dialog .el-pagination {
  color: #606266;
}
.face-device-v3-command-dialog .el-pagination button {
  width: 40px !important;
  height: 32px !important;
  color: #606266 !important;
  background: #f4f4f5 !important;
}
.face-device-v3-command-dialog .el-pagination button:disabled {
  color: #c0c4cc !important;
  background: #f4f4f5 !important;
}
.face-device-v3-command-dialog .el-pager li {
  width: auto !important;
  min-width: 35.5px;
  height: 32px !important;
  margin: 0 5px;
  border: 0 !important;
  line-height: 32px !important;
  color: #606266 !important;
  background: #f4f4f5 !important;
  opacity: 1;
}
.face-device-v3-command-dialog .el-pager li.active,
.face-device-v3-command-dialog .el-pager li:hover {
  color: #409eff !important;
}
.face-device-v3-command-dialog .el-pagination__total,
.face-device-v3-command-dialog .el-pagination__jump {
  color: #606266 !important;
}
.face-device-v3-command-dialog .el-pagination__jump input {
  color: #606266 !important;
  background: #fff !important;
  border-color: #dcdfe6 !important;
}
@media (max-width: 760px) {
  .face-device-v3-config-dialog {
    width: 94% !important;
  }
  .face-device-v3-config-dialog .config-grid {
    grid-template-columns: 1fr;
  }
}
</style>
